import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ColumnMode } from '@swimlane/ngx-datatable';
import { colors } from 'app/colors.const';
import { BiFinanceiroService } from 'app/services/bi-financeiro.service';

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

// tb_venda.id_sistema de cada dashboard, usado só para abrir a aba certa em Vendas e Boletos.
const SISTEMA_EMPRESA = { rastreei: 2, faleflex: 3 };

export const OPCOES_PERIODO = [
	{ valor: '2', nome: 'Últimos 2 meses' },
	{ valor: '3', nome: 'Últimos 3 meses' },
	{ valor: '6', nome: 'Últimos 6 meses' },
	{ valor: '12', nome: 'Últimos 12 meses' },
	{ valor: 'personalizado', nome: 'Período personalizado' },
];

export const DEFINICOES = {
	cobrado: 'Soma das cobranças emitidas no Efí para as competências do período, sem as canceladas. '
		+ 'Fórmula: Σ valor das cobranças não canceladas.',
	recebido: 'Valor pago das cobranças do período (Pago ou Liquidado no Efí). Fórmula: Σ valor das cobranças pagas. '
		+ 'Taxa de recebimento = recebido ÷ cobrado com situação conhecida × 100.',
	saldo: 'Valor ainda não pago das cobranças do período. Fórmula: a vencer + vencido. '
		+ 'Não inclui cobranças sem situação confirmada nem pagamentos em confirmação. Não é saldo bancário.',
	inadimplencia: 'Valor vencido e não pago. Fórmula: vencido não pago ÷ valor das cobranças já vencidas × 100. '
		+ 'Valores a vencer não entram; a variação é em pontos percentuais.',
};

const SITUACAO_COR = {
	'Quitado': 'success',
	'A vencer': 'info',
	'Com atraso': 'danger',
	'Situação não confirmada': 'secondary',
	'Em confirmação': 'primary',
	'Sem cobranças': 'secondary',
};

const COR_PAGADOR = { bom: 'success', regular: 'warning', inadimplente: 'danger', sem_historico: 'secondary' };

export const ABAS_RETENCAO = [
	{ id: 'aumentaram', nome: 'Aumentaram', campo: 'clientes_aumentaram' },
	{ id: 'reduziram', nome: 'Reduziram', campo: 'clientes_reduziram' },
	{ id: 'novos', nome: 'Novos', campo: 'clientes_novos' },
	{ id: 'perdidos', nome: 'Sem cobrança agora', campo: 'clientes_perdidos' },
];

export const DEFINICOES_CLIENTES = {
	bons_pagadores: 'Clientes que pagaram tudo o que já venceu ÷ clientes com histórico suficiente (cobranças já vencidas '
		+ 'na janela). Boleto pago com atraso conta como pago: o Efí não guarda a data na situação.',
	retencao: 'Clientes com cobrança no período anterior que também tiveram cobrança agora ÷ clientes do período anterior.',
	perdidos: 'Clientes do período anterior sem cobrança emitida no atual: saíram, foram bloqueados ou ainda não tiveram '
		+ 'a cobrança gerada. Receita = o que eles faturavam no período anterior.',
	novos: 'Clientes com cobrança no período atual e nenhuma no anterior.',
	nrr: 'Receita líquida retida: (faturamento anterior + aumentos − reduções − clientes sem cobrança) ÷ faturamento anterior. '
		+ 'Acima de 100%, a base antiga cresceu mesmo sem clientes novos.',
	grr: 'Receita bruta retida: o mesmo da NRR sem contar os aumentos. Mede só o quanto a base perdeu.',
	recorrente: 'Cobrado dos clientes que tiveram cobrança em todos os meses do período ÷ cobrado total.',
	concentracao: 'Participação dos maiores clientes no cobrado do período. HHI (0 a 10.000) acima de 1.500 indica receita concentrada.',
	volatilidade: 'Coeficiente de variação do cobrado mensal (desvio padrão ÷ média). Quanto menor, mais previsível. Precisa de 3 meses.',
	projecao: 'Estimativa: média do cobrado dos últimos 3 meses com dados × taxa de recebimento dos vencidos dos últimos meses '
		+ 'já fechados. Não é valor contratado nem saldo a receber.',
};

const PRIORIDADE = {
	critico: { nome: 'Crítico', cor: 'danger', icone: 'alert-octagon' },
	atencao: { nome: 'Atenção', cor: 'warning', icone: 'alert-triangle' },
	informativo: { nome: 'Informativo', cor: 'info', icone: 'info' },
};

export function rotuloCompetencia(competencia: string): string {
	if (!competencia) {
		return '';
	}
	const [ano, mes] = competencia.split('-');
	return `${MESES[Number(mes) - 1]}/${ano.slice(2)}`;
}

export function moeda(valor: number): string {
	return valor == null ? '—' : valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function moedaCurta(valor: number): string {
	if (valor == null) {
		return '';
	}
	const abs = Math.abs(valor);
	const numero = (v: number, casas: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: casas });
	if (abs >= 1e6) {
		return `R$ ${numero(valor / 1e6, 1)} mi`;
	}
	if (abs >= 1e3) {
		return `R$ ${numero(valor / 1e3, 1)} mil`;
	}
	return `R$ ${numero(valor, 0)}`;
}

export function percentual(valor: number, casas = 1): string {
	return valor == null ? '—' : `${valor.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })}%`;
}

// Cores do tema em uso (variáveis CSS do Bootstrap); o colors.const tem o roxo padrão do Vuexy, não o primário do sistema.
function corTema(nome: string): string {
	try {
		const valor = getComputedStyle(document.documentElement).getPropertyValue(`--${nome}`).trim();
		if (valor) {
			return valor;
		}
	} catch (e) { }
	return colors.solid[nome];
}

function somarMeses(competencia: string, meses: number): string {
	const [ano, mes] = competencia.split('-').map(Number);
	const indice = ano * 12 + mes - 1 + meses;
	return `${Math.floor(indice / 12)}-${String((indice % 12) + 1).padStart(2, '0')}`;
}

@Component({
	selector: 'app-bi-financeiro',
	templateUrl: './bi-financeiro.component.html',
	styleUrls: ['./bi-financeiro.component.scss'],
})
export class BiFinanceiroComponent implements OnInit {
	constructor(private route: ActivatedRoute, private router: Router, private biFinanceiroService: BiFinanceiroService) { }

	ColumnMode = ColumnMode;
	opcoesPeriodo = OPCOES_PERIODO;
	definicoes = DEFINICOES;
	definicoesClientes = DEFINICOES_CLIENTES;
	corPagador = COR_PAGADOR;
	abasRetencao = ABAS_RETENCAO;
	prioridades = PRIORIDADE;
	moeda = moeda;
	percentual = percentual;
	rotuloCompetencia = rotuloCompetencia;

	empresa: string;
	nomeEmpresa: string;
	contentHeader: object;

	carregando = false;
	erro: string = null;
	dados: any = null;
	consultadoEm: Date = null;

	opcaoPeriodo = '2';
	inicioPersonalizado = '';
	fimPersonalizado = '';
	erroPeriodo: string = null;
	comparar = true;

	graficoCobradoRecebido: any = null;
	graficoInadimplencia: any = null;
	graficoRecebimentos: any = null;

	buscaFechamento = '';
	filtroSituacao = '';
	situacoes: string[] = [];
	fechamentos: any[] = [];
	desempenho: any[] = [];

	abaPagadores = 'bom';
	linhasPagadores: any[] = [];
	abaRetencao = 'aumentaram';
	linhasRetencao: any[] = [];
	movimento: any[] = [];
	indicadoresOcultos: string[] = [];

	ngOnInit(): void {
		this.empresa = this.route.snapshot.data.empresa;
		this.nomeEmpresa = this.empresa === 'faleflex' ? 'FaleFlex' : 'Rastreei';
		this.contentHeader = {
			headerTitle: 'Dashboard Financeiro',
			actionButton: false,
			breadcrumb: {
				type: '',
				links: [
					{ name: 'Início', isLink: true, link: '/inicio' },
					{ name: 'BI Financeiro', isLink: false },
					{ name: this.nomeEmpresa, isLink: false },
				],
			},
		};
		this.restaurarFiltros();
		this.carregar();
	}

	// ---------- filtros ----------

	get ultimaCompetencia(): string {
		const lista = this.dados?.competencias_disponiveis || [];
		return lista.length ? lista[lista.length - 1].competencia : null;
	}

	get primeiraCompetencia(): string {
		const lista = this.dados?.competencias_disponiveis || [];
		return lista.length ? lista[0].competencia : null;
	}

	trocarPeriodo() {
		this.erroPeriodo = null;
		if (this.opcaoPeriodo === 'personalizado') {
			this.inicioPersonalizado = this.inicioPersonalizado || this.dados?.periodo?.inicio || '';
			this.fimPersonalizado = this.fimPersonalizado || this.dados?.periodo?.fim || '';
			return;
		}
		this.carregar();
	}

	aplicarPersonalizado() {
		this.erroPeriodo = null;
		if (!this.inicioPersonalizado || !this.fimPersonalizado) {
			this.erroPeriodo = 'Informe o mês inicial e o final.';
			return;
		}
		if (this.inicioPersonalizado > this.fimPersonalizado) {
			this.erroPeriodo = 'O mês inicial deve ser anterior ou igual ao final.';
			return;
		}
		this.carregar();
	}

	trocarComparacao() {
		this.salvarFiltros();
		this.montarGraficos();
	}

	private intervalo(): { inicio?: string, fim?: string } {
		if (this.opcaoPeriodo === 'personalizado') {
			return { inicio: this.inicioPersonalizado, fim: this.fimPersonalizado };
		}
		const ultima = this.ultimaCompetencia;
		if (!ultima) {
			return {}; // primeira carga: a API usa os 2 últimos meses com fechamento
		}
		return { inicio: somarMeses(ultima, 1 - Number(this.opcaoPeriodo)), fim: ultima };
	}

	// Cada empresa guarda os próprios filtros (Rastreei e FaleFlex são independentes).
	private chaveFiltros() {
		return `biFinanceiro.${this.empresa}`;
	}

	private salvarFiltros() {
		try {
			sessionStorage.setItem(this.chaveFiltros(), JSON.stringify({
				opcao: this.opcaoPeriodo, inicio: this.inicioPersonalizado, fim: this.fimPersonalizado, comparar: this.comparar,
			}));
		} catch (e) { }
	}

	private restaurarFiltros() {
		try {
			const salvo = JSON.parse(sessionStorage.getItem(this.chaveFiltros()) || 'null');
			if (salvo && OPCOES_PERIODO.some((o) => o.valor === salvo.opcao)) {
				this.opcaoPeriodo = salvo.opcao;
				this.inicioPersonalizado = salvo.inicio || '';
				this.fimPersonalizado = salvo.fim || '';
				this.comparar = salvo.comparar !== false;
			}
		} catch (e) { }
		if (this.opcaoPeriodo === 'personalizado' && (!this.inicioPersonalizado || !this.fimPersonalizado)) {
			this.opcaoPeriodo = '2';
		}
	}

	// ---------- carga ----------

	carregar() {
		const { inicio, fim } = this.intervalo();
		const opcaoSemBase = !inicio && this.opcaoPeriodo !== '2' && this.opcaoPeriodo !== 'personalizado';
		this.carregando = true;
		this.erro = null;
		this.biFinanceiroService.getDashboard(this.empresa, inicio, fim).subscribe(
			(res) => {
				this.dados = res.data;
				this.consultadoEm = new Date();
				this.carregando = false;
				// Filtro restaurado de "Últimos N meses" precisa da última competência, que só vem na primeira resposta.
				if (opcaoSemBase && this.ultimaCompetencia) {
					this.carregar();
					return;
				}
				this.salvarFiltros();
				this.montarDesempenho();
				this.prepararClientes();
				this.prepararTabela();
				this.montarGraficos();
			},
			(error) => {
				console.log(error);
				this.carregando = false;
				this.erro = this.mensagemErro(error);
			},
		);
	}

	// Os interceptors do sistema podem entregar o HttpErrorResponse, só a mensagem da API (string ou lista) ou,
	// com o throwError(() => erro) no rxjs 6, uma função que devolve o erro.
	private mensagemErro(error): string {
		if (typeof error === 'function') {
			error = error();
		}
		if (error?.status === 0) {
			return 'Não foi possível falar com a API. Verifique a conexão e tente de novo.';
		}
		const mensagem = typeof error === 'string' || Array.isArray(error) ? error : error?.error?.message;
		return (Array.isArray(mensagem) ? mensagem.join(' ') : mensagem) || 'Não foi possível carregar o BI Financeiro.';
	}

	get resumo() {
		return this.dados?.resumo;
	}

	get temQualidadeParcial(): boolean {
		const q = this.dados?.qualidade;
		return !!q && (q.sem_status.qtd > 0 || q.sem_valor > 0 || q.valor_estimado > 0 || q.duplicadas.length > 0);
	}

	// ---------- indicadores ----------

	// Variação com cor pela leitura do indicador: alta de cobrado é neutra, alta de recebido é boa,
	// alta de saldo e de inadimplência é ruim.
	chip(valor: number, formato: 'pct' | 'pp', sentido: 'neutro' | 'alta_boa' | 'alta_ruim', corRuim = 'danger') {
		if (valor == null || !isFinite(valor)) {
			return { texto: 'sem comparação', cor: 'secondary', direcao: 0 };
		}
		const direcao = Math.abs(valor) < 0.05 ? 0 : Math.sign(valor);
		const numero = Math.abs(valor).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
		const texto = `${direcao > 0 ? '+' : direcao < 0 ? '−' : ''}${numero}${formato === 'pp' ? ' p.p.' : '%'}`;
		let cor = 'primary';
		if (sentido !== 'neutro' && direcao !== 0) {
			const boa = sentido === 'alta_boa' ? direcao > 0 : direcao < 0;
			cor = boa ? 'success' : corRuim;
		} else if (direcao === 0) {
			cor = 'secondary';
		}
		return { texto, cor, direcao };
	}

	private montarDesempenho() {
		const d = this.dados?.desempenho;
		if (!d) {
			this.desempenho = [];
			this.indicadoresOcultos = [];
			return;
		}
		const tempo = d.tempo_recebimento.atual;
		const itens = [
			{ nome: 'Crescimento das cobranças', grupo: 'Faturamento', valor: d.crescimento_cobrancas_pct, tipo: 'variacao', sentido: 'neutro',
				ajuda: 'Variação do total cobrado sobre o período anterior.' },
			{ nome: 'Crescimento dos recebimentos', grupo: 'Recebimentos', valor: d.crescimento_recebimentos_pct, tipo: 'variacao', sentido: 'alta_boa',
				ajuda: 'Variação do total recebido sobre o período anterior. Em período com boletos a vencer, a queda é esperada.' },
			{ nome: 'Eficiência de cobrança', grupo: 'Eficiência', valor: d.eficiencia_pp, tipo: 'pp', sentido: 'alta_boa',
				ajuda: 'Variação, em p.p., do recebido sobre o que já venceu (recebido dos vencidos ÷ cobrado vencido).',
				extra: d.taxa_recebimento_vencidos.atual != null ? `${percentual(d.taxa_recebimento_vencidos.atual)} do vencido foi recebido` : null },
			{ nome: 'Taxa de recebimento', grupo: 'Eficiência', valor: d.taxa_recebimento.atual, tipo: 'pct',
				ajuda: 'Recebido ÷ cobrado com situação conhecida (inclui o que ainda vai vencer).',
				anterior: d.taxa_recebimento.anterior != null ? percentual(d.taxa_recebimento.anterior) : null },
			{ nome: 'Clientes pagantes', grupo: 'Recebimentos', valor: d.clientes_pagantes.atual, tipo: 'qtd',
				ajuda: 'Clientes com pelo menos uma cobrança paga no período.', anterior: String(d.clientes_pagantes.anterior) },
			{ nome: 'Ticket médio por cobrança', grupo: 'Faturamento', valor: d.ticket_medio.atual, tipo: 'moeda',
				ajuda: 'Cobrado ÷ quantidade de cobranças com valor.', anterior: d.ticket_medio.anterior != null ? moeda(d.ticket_medio.anterior) : null },
			{ nome: 'Tempo médio de recebimento', grupo: 'Eficiência', valor: tempo.tempo_medio_dias, tipo: 'dias',
				ajuda: `Dias entre a emissão do boleto e o aviso de pagamento do webhook (${tempo.amostra} pagamento(s) com data).` },
			{ nome: 'Recebidas no prazo', grupo: 'Eficiência', valor: tempo.pct_no_prazo, tipo: 'pct',
				ajuda: `Pagamentos com data até o vencimento ÷ pagamentos com data (${tempo.amostra}).` },
		];
		// Só os indicadores calculáveis com os dados do período.
		this.desempenho = itens.filter((i) => i.valor != null && isFinite(i.valor));
		const ocultos = [];
		if (d.tempo_recebimento.atual.tempo_medio_dias == null) {
			ocultos.push('tempo médio de recebimento');
		}
		if (d.tempo_recebimento.atual.pct_no_prazo == null) {
			ocultos.push('recebidas no prazo');
		}
		if (d.taxa_recebimento.atual == null) {
			ocultos.push('taxa de recebimento');
		}
		this.indicadoresOcultos = ocultos;
	}

	valorIndicador(i): string {
		switch (i.tipo) {
			case 'moeda': return moeda(i.valor);
			case 'pct': return percentual(i.valor);
			case 'qtd': return String(i.valor);
			case 'dias': return `${i.valor.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} dia(s)`;
			default: return this.chip(i.valor, i.tipo === 'pp' ? 'pp' : 'pct', i.sentido).texto;
		}
	}

	formatar(campo): string {
		if (!campo || campo.valor == null) {
			return '—';
		}
		switch (campo.formato) {
			case 'moeda': return moeda(campo.valor);
			case 'pct': return percentual(campo.valor);
			case 'pp': return this.chip(campo.valor, 'pp', 'neutro').texto;
			default: return String(campo.valor);
		}
	}

	// ---------- tabela de fechamentos ----------

	private prepararTabela() {
		this.situacoes = [...new Set<string>((this.dados?.fechamentos || []).map((f) => f.situacao))].sort();
		if (this.filtroSituacao && !this.situacoes.includes(this.filtroSituacao)) {
			this.filtroSituacao = '';
		}
		this.filtrarFechamentos();
	}

	filtrarFechamentos() {
		const busca = this.buscaFechamento.trim().toLowerCase();
		this.fechamentos = (this.dados?.fechamentos || [])
			.map((f) => ({ ...f, rotulo: rotuloCompetencia(f.competencia), cor: SITUACAO_COR[f.situacao] || 'secondary' }))
			.filter((f) => !this.filtroSituacao || f.situacao === this.filtroSituacao)
			.filter((f) => !busca || `${f.id_fechamento} ${f.rotulo} ${f.competencia} ${f.situacao}`.toLowerCase().includes(busca));
	}

	abrirFechamento(id: number) {
		this.router.navigate(['/fechamento', id, 'vendas'], { queryParams: { sistema: SISTEMA_EMPRESA[this.empresa] } });
	}

	// ---------- clientes ----------

	private prepararClientes() {
		// Aba vazia (ex.: FaleFlex sem situação confirmada): abre a primeira classificação com clientes.
		const resumo = this.dados?.pagadores?.resumo || [];
		if (!this.qtdPagadores(this.abaPagadores) && resumo.some((r) => r.clientes)) {
			this.abaPagadores = resumo.find((r) => r.clientes).classe;
		}
		this.trocarAbaPagadores(this.abaPagadores);
		this.trocarAbaRetencao(this.abaRetencao);
		const r = this.dados?.retencao?.receita;
		this.movimento = !r ? [] : [
			{ nome: 'Faturamento do período anterior', valor: r.anterior, sinal: '', cor: '' },
			{ nome: 'Clientes novos', valor: r.novos, sinal: '+', cor: 'text-success' },
			{ nome: 'Aumento dos clientes que ficaram', valor: r.expansao, sinal: '+', cor: 'text-success' },
			{ nome: 'Redução dos clientes que ficaram', valor: r.contracao, sinal: '−', cor: 'text-warning' },
			{ nome: 'Clientes sem cobrança agora', valor: r.perdida, sinal: '−', cor: 'text-danger' },
			{ nome: 'Faturamento do período atual', valor: r.atual, sinal: '=', cor: 'font-weight-bolder' },
		];
	}

	trocarAbaPagadores(classe: string) {
		this.abaPagadores = classe;
		this.linhasPagadores = (this.dados?.pagadores?.clientes || []).filter((c) => c.classe === classe);
	}

	trocarAbaRetencao(id: string) {
		this.abaRetencao = id;
		const aba = ABAS_RETENCAO.find((a) => a.id === id);
		this.linhasRetencao = (aba && this.dados?.retencao?.[aba.campo]) || [];
	}

	qtdPagadores(classe: string): number {
		return this.dados?.pagadores?.resumo?.find((r) => r.classe === classe)?.clientes || 0;
	}

	qtdRetencao(campo: string): number {
		return this.dados?.retencao?.[campo]?.length || 0;
	}

	ativarLinha(evento) {
		if (evento.type === 'click' && evento.row) {
			this.abrirFechamento(evento.row.id_fechamento);
		}
	}

	exportarCsv() {
		const num = (v: number) => (v == null ? '' : v.toFixed(2).replace('.', ','));
		const linhas = [
			['Data do fechamento', 'Fechamento', 'Competência', 'Cobrado', 'Recebido', 'Saldo em aberto', 'Saldo vencido',
				'Em confirmação', 'Sem situação confirmada', 'Cobranças', 'Situação'],
			...this.fechamentos.map((f) => [
				f.dt_fechamento ? f.dt_fechamento.split('-').reverse().join('/') : '', f.id_fechamento, f.rotulo,
				num(f.cobrado), num(f.recebido), num(f.saldo_aberto), num(f.vencido), num(f.em_confirmacao), num(f.sem_status),
				f.qtd, f.situacao,
			]),
		];
		const csv = linhas.map((l) => l.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\r\n');
		const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
		const link = document.createElement('a');
		link.href = URL.createObjectURL(blob);
		link.download = `fechamentos_${this.empresa}_${this.dados.periodo.inicio}_${this.dados.periodo.fim}.csv`;
		link.click();
		URL.revokeObjectURL(link.href);
	}

	// ---------- gráficos ----------

	private mesesGrafico() {
		const mensal = this.dados?.mensal || [];
		return this.comparar ? mensal : mensal.filter((m) => m.periodo === 'atual');
	}

	private categorias(meses) {
		return meses.map((m) => rotuloCompetencia(m.competencia) + (m.em_andamento ? '*' : ''));
	}

	// Meses do período anterior em tom claro: barras com cor por ponto, linhas com marcadores claros.
	private corBarra(meses, cor: string) {
		return ({ dataPointIndex }) => (meses[dataPointIndex]?.periodo === 'anterior' ? cor + '59' : cor);
	}

	private marcadoresAnteriores(meses, serie: number, cor: string) {
		return meses
			.map((m, i) => ({ m, i }))
			.filter(({ m }) => m.periodo === 'anterior')
			.map(({ i }) => ({ seriesIndex: serie, dataPointIndex: i, fillColor: '#fff', strokeColor: cor, size: 4 }));
	}

	private base(tipo: string, altura = 300) {
		return {
			chart: { type: tipo, height: altura, toolbar: { show: false }, fontFamily: 'inherit', zoom: { enabled: false } },
			noData: { text: 'Sem dados no período' },
			grid: { borderColor: '#ebe9f1', strokeDashArray: 4, padding: { left: 8, right: 8 } },
			legend: { show: true, position: 'top', horizontalAlign: 'left' },
			dataLabels: { enabled: false },
		};
	}

	montarGraficos() {
		if (!this.dados || this.dados.sem_dados) {
			this.graficoCobradoRecebido = this.graficoInadimplencia = this.graficoRecebimentos = null;
			return;
		}
		const meses = this.mesesGrafico();
		const categorias = this.categorias(meses);
		const [primaria, sucesso, perigo, alerta] = ['primary', 'success', 'danger', 'warning'].map(corTema);
		const tooltipMoeda = { shared: true, intersect: false, y: { formatter: (v) => moeda(v) } };

		this.graficoCobradoRecebido = {
			...this.base('bar'),
			series: [
				{ name: 'Total cobrado', data: meses.map((m) => m.qtd ? m.cobrado : null) },
				{ name: 'Total recebido', data: meses.map((m) => m.qtd ? m.recebido : null) },
			],
			colors: [this.corBarra(meses, primaria), this.corBarra(meses, sucesso)],
			plotOptions: { bar: { columnWidth: '55%', borderRadius: 4 } },
			stroke: { show: true, width: 2, colors: ['transparent'] },
			xaxis: { categories: categorias },
			yaxis: { labels: { formatter: (v) => moedaCurta(v) } },
			tooltip: tooltipMoeda,
			fill: { opacity: 1 },
			markers: {},
		};

		this.graficoInadimplencia = {
			...this.base('line'),
			series: [
				{ name: 'Vencido e não pago', type: 'column', data: meses.map((m) => m.qtd ? m.vencido : null) },
				{ name: 'Inadimplência (%)', type: 'line', data: meses.map((m) => m.inadimplencia_pct) },
			],
			colors: [this.corBarra(meses, alerta), perigo],
			plotOptions: { bar: { columnWidth: '45%', borderRadius: 4 } },
			stroke: { width: [0, 3], curve: 'smooth' },
			markers: { size: [0, 4], discrete: this.marcadoresAnteriores(meses, 1, perigo) },
			xaxis: { categories: categorias },
			yaxis: [
				{ labels: { formatter: (v) => moedaCurta(v) } },
				{ opposite: true, min: 0, tickAmount: 4, forceNiceScale: true, labels: { formatter: (v) => v == null ? '' : percentual(v, 1) } },
			],
			tooltip: {
				shared: true, intersect: false,
				y: { formatter: (v, { seriesIndex }) => (v == null ? 'sem base' : seriesIndex === 1 ? percentual(v, 2) : moeda(v)) },
			},
			fill: { opacity: 1 },
		};

		this.graficoRecebimentos = {
			...this.base('line'),
			series: [
				{ name: 'Recebido', type: 'area', data: meses.map((m) => m.qtd ? m.recebido : null) },
				{ name: 'Variação sobre o mês anterior (%)', type: 'line', data: meses.map((m) => m.variacao_recebido_pct) },
			],
			colors: [sucesso, primaria],
			stroke: { width: [3, 2], curve: 'smooth', dashArray: [0, 5] },
			fill: { type: 'solid', opacity: [0.12, 1] },
			markers: { size: [4, 4], discrete: this.marcadoresAnteriores(meses, 0, sucesso) },
			plotOptions: {},
			xaxis: { categories: categorias },
			yaxis: [
				{ labels: { formatter: (v) => moedaCurta(v) } },
				{ opposite: true, tickAmount: 4, forceNiceScale: true, labels: { formatter: (v) => v == null ? '' : percentual(v, 0) } },
			],
			tooltip: {
				shared: true, intersect: false,
				y: { formatter: (v, { seriesIndex }) => (v == null ? 'não comparável' : seriesIndex === 1 ? percentual(v) : moeda(v)) },
			},
		};
	}

	get temMesEmAndamento(): boolean {
		return this.mesesGrafico().some((m) => m.em_andamento);
	}

	// ---------- inadimplência e previsão ----------

	larguraFaixa(f): number {
		return Math.max(0, Math.min(100, f.participacao_pct || 0));
	}

	// 'AAAA-MM-DD HH:mm:ss' do banco -> 'dd/mm/aaaa hh:mm'
	dataHora(valor: string): string {
		if (!valor) {
			return '—';
		}
		const [data, hora = ''] = valor.split(' ');
		return `${data.split('-').reverse().join('/')} ${hora.slice(0, 5)}`.trim();
	}

	data(valor: string): string {
		return valor ? valor.slice(0, 10).split('-').reverse().join('/') : '—';
	}
}
